// src/utils/markerUtils.ts
import { icon, divIcon } from 'leaflet';

export const MARKER_COLORS_OLD = {
  red: '#FF3B30',
  yellow: '#FFCC00',
  green: '#34C759',
  blue: '#007AFF',
  lightblue: '#66b0ff',
  purple: '#5856D6',
  orange: '#FF9500',
  pink: '#FF2D55'
} as const;

export const MARKER_COLORS_OLD2 = {
  blue: '#4285F4',
  green: '#19A673',
  purple: '#B56AFF',
  red: '#F74A56',
  yellow: '#FBC02D',
  orange: '#FF8127',
  pink: '#F848C7',
  bloodRed: '#EC191D'
} as const;

export const MARKER_COLORS = {
  blue: '#A8C5FF',    // Light sky blue
  green: '#8FD4BA',   // Soft mint green
  purple: '#DDB5FF',  // Lavender
  red: '#FFB3B8',     // Soft coral
  yellow: '#FFE4A1',  // Pale yellow
  orange: '#FFD0B5',  // Peach
  pink: '#FFC4E8',    // Baby pink
  bloodRed: '#FF9FA1' // Dusty rose
} as const;

export type MarkerColorKey = keyof typeof MARKER_COLORS;

export const COLOR_SEQUENCE: MarkerColorKey[] = [
  'blue', 'green', 'purple', 'red', 'yellow', 'orange', 'pink', 'bloodRed'
];

// Keep track of seen markers to know which ones are new
const seenMarkerIds = new Set<string>();

interface CreatePinOptions {
  number?: number;
  color?: MarkerColorKey;
  scale?: number;     // Scale factor for the pin size
  animated?: boolean; // Whether to animate the pin
  id?: string;        // ID of the marker to track if it's new
  zIndex?: number; // Add this line
}

// Reset the seen markers when needed (e.g., when changing locations completely)
export function resetSeenMarkers() {
  seenMarkerIds.clear();
}


  export function createPin({ number, color, scale = 1, animated = false, id = '', zIndex = 400 // Default z-index
  }: CreatePinOptions = {}) {
    const markerColor = color 
      ? MARKER_COLORS[color]
      : number 
        ? MARKER_COLORS[COLOR_SEQUENCE[(number - 1) % COLOR_SEQUENCE.length]]
        : MARKER_COLORS.blue;
  
    // Base dimensions that will be scaled
    const baseWidth = 40;
    const baseHeight = 56;
    const actualWidth = baseWidth * scale;
    const actualHeight = baseHeight * scale;
    
    const displayNumber = number && number < 10 ? number : number?.toString().padStart(2, '0');
    
    // If this marker has an ID, add it to the seen set
    if (id) {
      seenMarkerIds.add(id);
    }
    
    return icon({
      iconUrl: `data:image/svg+xml;base64,${btoa(`
        <svg 
          width="${actualWidth}" 
          height="${actualHeight}" 
          viewBox="0 0 46 60" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <!-- Filters -->
          <defs>
            <filter id="dropShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
              <feOffset dx="1" dy="1" result="offsetblur"/>
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.5"/>
              </feComponentTransfer>
              <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            
            <filter id="groundShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            </filter>
            
            <linearGradient id="glossGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.3"/>
              <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/>
            </linearGradient>
            
            <linearGradient id="shineGradient" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.7"/>
              <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/>
            </linearGradient>
          </defs>
          
          <!-- Ground Shadow -->
          <ellipse 
            cx="23" 
            cy="56" 
            rx="10" 
            ry="4" 
            fill="rgba(0,0,0,0.3)" 
            filter="url(#groundShadow)" 
          />
          
          <!-- Pin Body with Stroke and Shadow -->
          <path 
            d="M23 3C14.2 3 7 10.2 7 19C7 31 23 47 23 47C23 47 39 31 39 19C39 10.2 31.8 3 23 3Z" 
            fill="${markerColor}"
            stroke="#FFFFFFFF"
            stroke-width="3"
            stroke-linejoin="round"
            filter="url(#dropShadow)"
          />
    
          ${!number ? `
            <!-- Glossy overlay for unnumbered pins -->
            <path 
              d="M23 3C14.2 3 7 10.2 7 19C7 31 23 47 23 47C23 47 39 31 39 19C39 10.2 31.8 3 23 3Z" 
              fill="url(#glossGradient)"
            />
            <path 
              d="M23 3C14.2 3 7 10.2 7 19C7 31 23 47 23 47C23 47 39 31 39 19C39 10.2 31.8 3 23 3Z" 
              fill="url(#shineGradient)" 
              opacity="0.3"
            />
          ` : `
           
            <!-- Text for numbered pins -->
             <text 
              x="50%" 
              y="50%" 
              dy="-9" 
              dx="-0.7"
              font-family="Helvetica, sans-serif" 
              font-size="${number && number > 9 ? '12' : '12'}" 
              font-weight="bold" 
              fill="#2C3E50" 
              text-anchor="middle" 
              dominant-baseline="middle"
              letter-spacing="-2" 
            >
              ${displayNumber}
            </text>
          `}
        </svg>
      `)}`,
      iconSize: [actualWidth, actualHeight],
      iconAnchor: [actualWidth / 2, actualHeight],
      popupAnchor: [0, -actualHeight]
    });
    
  }
  
  // Simplified to remove animations, just use the regular pin creation
  export function createAnimatedPin(options: CreatePinOptions = {}) {
    return createPin(options);
  }


   /* <!-- Circle background for number -->
            <circle 
              cx="23" 
              cy="19" 
              r="11"
              fill="white"
              stroke="#00000022"
              stroke-width="1"
            /> */